import { useNavigate } from 'react-router-dom';
import { Paper, Title, Stack, Group, Text, Badge, Button, Box, UnstyledButton } from '@mantine/core';
import { IconBookmark, IconBox, IconClipboardCheck, IconExternalLink } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useDashboardViews, type SavedFilter } from '@/hooks/api/useSavedFilters';

const entityIcons = {
  assets: IconBox,
  audits: IconClipboardCheck,
  users: IconClipboardCheck,
};

const entityRoutes = {
  assets: '/assets',
  audits: '/audits',
  users: '/settings/users',
};

const entityColors = {
  assets: 'blue',
  audits: 'green',
  users: 'violet',
};

interface ViewItemProps {
  view: SavedFilter;
  onClick: () => void;
}

function ViewItem({ view, onClick }: ViewItemProps) {
  const Icon = entityIcons[view.entity_type] || IconBox;
  const color = entityColors[view.entity_type] || 'gray';

  return (
    <UnstyledButton onClick={onClick} style={{ width: '100%' }}>
      <Box
        p="sm"
        style={{
          borderRadius: 'var(--mantine-radius-md)',
          border: '1px solid var(--mantine-color-gray-2)',
          transition: 'all 0.15s ease',
          cursor: 'pointer',
        }}
        className="hover-card"
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Icon size={18} color={`var(--mantine-color-${color}-6)`} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} truncate>
                {view.name}
              </Text>
              {view.description && (
                <Text size="xs" c="dimmed" truncate>
                  {view.description}
                </Text>
              )}
            </div>
          </Group>
          <Badge size="xs" color={color} variant="light">
            {view.entity_type}
          </Badge>
        </Group>
      </Box>
    </UnstyledButton>
  );
}

export function SavedViewsCard() {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const { data: views = [], isLoading } = useDashboardViews(organizationId || '');

  const handleViewClick = (view: SavedFilter) => {
    // Navigate to the entity page with the filter applied
    const baseRoute = entityRoutes[view.entity_type];
    // Encode the filter config as a query param
    const filterParam = encodeURIComponent(JSON.stringify(view.filter_config));
    navigate(`${baseRoute}?savedFilter=${view.id}&filterConfig=${filterParam}`);
  };

  if (isLoading) {
    return null;
  }

  if (views.length === 0) {
    return null; // Don't show if no views are configured for dashboard
  }

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconBookmark size={20} />
          <Title order={4}>Saved Views</Title>
        </Group>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate('/assets')}
        >
          Manage
        </Button>
      </Group>

      <Stack gap="xs">
        {views.map((view) => (
          <ViewItem key={view.id} view={view} onClick={() => handleViewClick(view)} />
        ))}
      </Stack>

      <style>{`
        .hover-card:hover {
          background-color: var(--mantine-color-gray-0);
          border-color: var(--mantine-color-gray-4);
        }
      `}</style>
    </Paper>
  );
}
